import { IWorkspaceResourceSlide, IWorkspaceResourceSlideTemplate, WorkspaceResourceType } from "common/interfaces";
import PresentationLibraryController from "js/controllers/PresentationLibraryController";
import WorkspaceController from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import * as appSearch from "js/core/services/appSearch";

import DataService from "./DataService";

interface TeamSlidesDataServiceSearchQuery {
    resourceTypes?: WorkspaceResourceType[],
    fullTextSearchQuery?: string,
    fullTextSearchByFields?: string[],
    tag?: string,
    size?: number,
    minScore?: number,
}

interface TeamSlidesDataServiceDataItem {
    id: string,
    libraryItemId: string,
    workspaceId: string,
    resource: IWorkspaceResourceSlide | IWorkspaceResourceSlideTemplate,
    isTemplate: boolean,
    isUsedInPresentations: boolean,
    isDisabled: boolean,
    name: string,
    description: string,
    tags: string[],
    presentationCount: number,
    ratedByCount: number,
    ratedByUsers: {
        [userUid: string]: boolean
    },
    createdAt: number,
    modifiedAt?: number,
    contentModifiedAt?: number,
    createdBy: string,
    score: number
}

class TeamSlidesDataService extends DataService {
    public async search(query: TeamSlidesDataServiceSearchQuery): Promise<TeamSlidesDataServiceDataItem[]> {
        const {
            fullTextSearchQuery,
            fullTextSearchByFields = [],
            tag,
            resourceTypes = [WorkspaceResourceType.SLIDE, WorkspaceResourceType.SLIDE_TEMPLATE],
            size = TeamSlidesDataService.SEARCH_QUERY_SIZE,
            minScore = TeamSlidesDataService.SEARCH_QUERY_MIN_SCORE,
        } = query;

        const trashedPresentationIds = PresentationLibraryController.getPresentations(AppController.workspaceId)
            .filter(presentation => presentation.trashedAt).map(presentation => presentation.id);

        const resourceToSearchResult = (resource: IWorkspaceResourceSlide | IWorkspaceResourceSlideTemplate, score: number): TeamSlidesDataServiceDataItem => {
            const usedPresentationIds = Object.keys(resource.metadata.usedInPresentations ?? {})
                .filter(presentationId => !trashedPresentationIds.includes(presentationId));

            return {
                id: resource.resourceId,
                libraryItemId: resource.resourceId,
                workspaceId: AppController.workspaceId,
                resource,
                isTemplate: resource.resourceType === WorkspaceResourceType.SLIDE_TEMPLATE,
                isUsedInPresentations: usedPresentationIds.length > 0,
                isDisabled: !!resource.metadata.isDisabled,
                name: resource.metadata.name,
                description: resource.metadata.description,
                tags: resource.tags,
                presentationCount: usedPresentationIds.length,
                ratedByUsers: resource.metadata.ratedByUsers.reduce((acc, uid) => ({ ...acc, [uid]: true }), {}),
                ratedByCount: resource.metadata.ratedByUsers.length,
                createdAt: resource.createdAt,
                modifiedAt: resource.modifiedAt,
                contentModifiedAt: resource.metadata.contentModifiedAt,
                createdBy: resource.metadata.createdBy,
                score
            };
        };

        if (!fullTextSearchQuery && !tag) {
            return WorkspaceController.resources
                .filter(r => resourceTypes.includes(r.resourceType))
                .map(r => resourceToSearchResult(r as IWorkspaceResourceSlide | IWorkspaceResourceSlideTemplate, Number.POSITIVE_INFINITY))
                .sort((a, b) => (b.modifiedAt ?? b.createdAt) - (a.modifiedAt ?? a.createdAt))
                .slice(0, size);
        }

        // Perform search
        let { results: appsearchResults } = await appSearch.search({
            workspaceId: AppController.workspaceId,
            searchEngine: appSearch.SearchEngine.SHARED_SLIDES,
            query: fullTextSearchQuery ?? "",
            page: { size },
            filters: { org_id: [AppController.workspaceId] },
            searchFields: fullTextSearchByFields.length > 0 ? fullTextSearchByFields.reduce((searchFields, fieldName) => ({ ...searchFields, [fieldName]: {} }), {}) : undefined
        });

        appsearchResults = appsearchResults
            // Filter by score to avoid excessive library items loading
            .filter(result => result._meta.score >= minScore);

        let searchResults: TeamSlidesDataServiceDataItem[] = await Promise.all(appsearchResults.map(async ({
            id: { raw: documentId },
            _meta: { score }
        }): Promise<TeamSlidesDataServiceDataItem | null> => {
            const slideId = documentId.split("/").pop();

            const resource = WorkspaceController.resources.find(r => r.resourceId === slideId && resourceTypes.includes(r.resourceType)) as IWorkspaceResourceSlide | IWorkspaceResourceSlideTemplate;
            if (!resource) {
                return null;
            }

            return resourceToSearchResult(resource, score);
        }));

        searchResults = searchResults
            // Remove non existing items
            .filter(item => !!item)
            // Filter by tag if specified to ensure consistency with live data
            // This prevents stale App Search results from returning items that no longer have the tag
            .filter(item => !tag || item.tags.includes(tag))
            .sort((a, b) => b.score - a.score);

        return searchResults;
    }
}

export default TeamSlidesDataService;
